import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Maximize2, ChevronDown } from 'lucide-react';
import { PASSPORT_STATUS, PASSPORT_CATEGORIES, SPEC_SECTIONS, TRANSPARENCY_TEXTS, fmtPrice, fmtDate, imgUrl } from '../../lib/boats';

/* ---------------- Gallery ---------------- */
export const BoatGallery = ({ photos = [], title, videoUrl }) => {
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);
  const has = photos.length > 0;
  const go = (d) => setIdx((i) => (i + d + photos.length) % photos.length);
  useEffect(() => {
    if (!full) return undefined;
    const n = photos.length;
    const onKey = (e) => {
      if (e.key === 'Escape') setFull(false);
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % n);
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + n) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full, photos.length]);

  return (
    <div className="space-y-3" data-testid="boat-gallery">
      <div className="relative aspect-[16/10] bg-[#302f2c] rounded-2xl overflow-hidden border border-[#3f4816]">
        {has ? (
          <>
            <img src={imgUrl(photos[idx].url)} alt={photos[idx].alt || `${title} — photo ${idx + 1}`} className="w-full h-full object-cover" width={photos[idx].width} height={photos[idx].height} fetchPriority={idx === 0 ? 'high' : undefined} />
            {photos.length > 1 && (
              <>
                <button type="button" onClick={() => go(-1)} aria-label="Photo précédente" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#1a1c1b]/80 text-[#dfddd6] flex items-center justify-center hover:bg-[#d9fb06] hover:text-[#1a1c1b]"><ChevronLeft /></button>
                <button type="button" onClick={() => go(1)} aria-label="Photo suivante" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#1a1c1b]/80 text-[#dfddd6] flex items-center justify-center hover:bg-[#d9fb06] hover:text-[#1a1c1b]"><ChevronRight /></button>
              </>
            )}
            <button type="button" onClick={() => setFull(true)} aria-label="Afficher en plein écran" className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-2 rounded-full bg-[#1a1c1b]/80 text-[#dfddd6] text-xs font-bold hover:bg-[#d9fb06] hover:text-[#1a1c1b]" data-testid="gallery-fullscreen"><Maximize2 size={14} /> {idx + 1} / {photos.length}</button>
          </>
        ) : <div className="w-full h-full flex items-center justify-center text-[#888680]">Photos à venir</div>}
      </div>
      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" role="list">
          {photos.map((p, i) => (
            <button key={p.id || i} type="button" onClick={() => setIdx(i)} aria-label={`Voir la photo ${i + 1}`} aria-current={i === idx} className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 ${i === idx ? 'border-[#d9fb06]' : 'border-transparent opacity-70 hover:opacity-100'}`}>
              <img src={imgUrl(p.thumb_url || p.url)} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {videoUrl && (
        <div className="aspect-video rounded-2xl overflow-hidden border border-[#3f4816] bg-black" data-testid="boat-video">
          {/youtube|youtu\.be|vimeo/.test(videoUrl)
            ? <iframe title={`Vidéo — ${title}`} src={videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/').replace('vimeo.com/', 'player.vimeo.com/video/')} className="w-full h-full" allow="fullscreen" loading="lazy" />
            : <video src={videoUrl} controls className="w-full h-full" preload="none" />}
        </div>
      )}
      {full && has && (
        <div className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center" role="dialog" aria-modal="true" aria-label="Galerie plein écran">
          <button type="button" onClick={() => setFull(false)} aria-label="Fermer" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-[#302f2c] text-[#dfddd6] flex items-center justify-center" data-testid="gallery-close"><X /></button>
          <button type="button" onClick={() => go(-1)} aria-label="Précédente" className="absolute left-4 w-12 h-12 rounded-full bg-[#302f2c] text-[#dfddd6] flex items-center justify-center"><ChevronLeft /></button>
          <img src={imgUrl(photos[idx].url)} alt={photos[idx].alt || title} className="max-w-[92vw] max-h-[88vh] object-contain" />
          <button type="button" onClick={() => go(1)} aria-label="Suivante" className="absolute right-4 w-12 h-12 rounded-full bg-[#302f2c] text-[#dfddd6] flex items-center justify-center"><ChevronRight /></button>
          <p className="absolute bottom-4 text-[#dfddd6] text-sm">{idx + 1} / {photos.length}</p>
        </div>
      )}
    </div>
  );
};

/* ---------------- Passport ---------------- */
export const BoatPassport = ({ passport = [] }) => {
  const entries = passport.filter((p) => p.status && PASSPORT_CATEGORIES[p.category]);
  if (!entries.length) return null;
  return (
    <section className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-6 md:p-8" aria-labelledby="passport-title" data-testid="boat-passport">
      <h2 id="passport-title" className="text-xl md:text-2xl font-black text-[#d9fb06] uppercase">Passeport ValidatedBoat</h2>
      <p className="text-sm text-[#888680] mt-2 mb-6">Ce passeport précise l'origine et le niveau de vérification de chaque information. Il ne constitue ni une expertise maritime officielle, ni une certification technique, ni une garantie.</p>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {entries.map((p) => {
          const st = PASSPORT_STATUS[p.status] || PASSPORT_STATUS.non_communique;
          return (
            <li key={p.category} className="bg-[#1a1c1b] border border-[#3f4816] rounded-xl p-4" data-testid={`passport-${p.category}`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-[#dfddd6]">{PASSPORT_CATEGORIES[p.category]}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
              </div>
              {p.note && <p className="text-sm text-[#dfddd6]/90 leading-relaxed">{p.note}</p>}
              <p className="text-xs text-[#888680] mt-2">{[p.source && `Source : ${p.source}`, p.updated_at && `Mis à jour le ${fmtDate(p.updated_at)}`].filter(Boolean).join(' · ')}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

/* ---------------- Specs accordions ---------------- */
const Accordion = ({ title, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#3f4816] rounded-xl overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between p-4 text-left font-bold text-[#dfddd6] hover:bg-[#3f4816]/20">
        {title}<ChevronDown size={18} className={`text-[#d9fb06] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="p-4 pt-0 text-sm">{children}</div>}
    </div>
  );
};

const KV = ({ rows }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
    {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 border-b border-[#3f4816]/60 py-1.5"><dt className="text-[#888680]">{k}</dt><dd className="text-[#dfddd6] text-right">{v}</dd></div>)}
  </dl>
);
const ListBlock = ({ items }) => <ul className="list-disc pl-5 space-y-1 text-[#dfddd6]">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>;

export const BoatSpecs = ({ boat }) => {
  const b = boat;
  const rows = (pairs) => pairs.filter(([, v]) => v !== null && v !== undefined && v !== '');
  const gen = rows([['Marque', b.brand], ['Modèle', b.model], ['Version', b.version], ['Chantier', b.builder], ['Architecte', b.architect], ['Année', b.year], ['Matériau de coque', b.hull_material], ['Pavillon', b.flag], ['TVA', b.vat_paid], ['Prix négociable', b.price_negotiable === true ? 'Oui' : b.price_negotiable === false ? 'Non' : null]]);
  const dim = rows([['Longueur', b.length && `${b.length} m`], ['Largeur', b.beam && `${b.beam} m`], ["Tirant d'eau", b.draft && `${b.draft} m`], ["Tirant d'air", b.air_draft && `${b.air_draft} m`], ['Déplacement', b.displacement && `${b.displacement} kg`], ['Cabines', b.cabins], ['Couchages', b.berths], ['Capacité', b.capacity && `${b.capacity} pers.`]]);
  const eng = rows([['Type', b.engine_type], ['Marque', b.engine_brand], ['Modèle', b.engine_model], ['Puissance', b.engine_power], ['Nombre', b.engine_count], ['Énergie', b.fuel], ['Heures', b.engine_hours != null && `${b.engine_hours} h`], ['Carburant', b.fuel_capacity && `${b.fuel_capacity} L`], ['Eau', b.water_capacity && `${b.water_capacity} L`]]);
  const specs = b.specs || {};
  const custom = (key) => (Array.isArray(specs[key]) ? specs[key].filter((r) => r?.label && r?.value).map((r) => [r.label, r.value]) : []);
  const sections = [
    ['generalites', [...gen, ...custom('generalites')]], ['dimensions', [...dim, ...custom('dimensions')]], ['coque', custom('coque')], ['motorisation', [...eng, ...custom('motorisation')]],
    ['greement', custom('greement')], ['electricite', custom('electricite')], ['electronique', custom('electronique')], ['interieur', custom('interieur')], ['confort', custom('confort')], ['securite', custom('securite')],
  ].filter(([, r]) => r.length);
  const lists = [['Inventaire', b.inventory], ['Équipements', b.equipment], ['Travaux effectués', b.works_done], ['Travaux à prévoir', b.works_todo]].filter(([, l]) => l?.length);
  const docs = (b.documents || []).filter((d) => d.public !== false);
  if (!sections.length && !lists.length && !docs.length) return null;
  return (
    <section aria-labelledby="specs-title" data-testid="boat-specs">
      <h2 id="specs-title" className="text-xl md:text-2xl font-black text-[#dfddd6] uppercase mb-4">Caractéristiques techniques</h2>
      <div className="space-y-2">
        {sections.map(([k, r], i) => <Accordion key={k} title={SPEC_SECTIONS[k]} defaultOpen={i === 0} testId={`spec-${k}`}><KV rows={r} /></Accordion>)}
        {lists.map(([t, l]) => <Accordion key={t} title={t}><ListBlock items={l} /></Accordion>)}
        {docs.length > 0 && <Accordion title="Documents disponibles" testId="spec-documents"><ul className="space-y-1">{docs.map((d) => <li key={d.id || d.label}>{d.url ? <a href={imgUrl(d.url)} target="_blank" rel="noopener noreferrer" className="text-[#d9fb06] underline">{d.label}</a> : <span className="text-[#dfddd6]">{d.label}</span>}</li>)}</ul></Accordion>}
      </div>
    </section>
  );
};

/* ---------------- Maintenance timeline ---------------- */
export const BoatTimeline = ({ items = [] }) => {
  const list = items.filter((m) => m?.year && m?.label).sort((a, b) => String(b.year).localeCompare(String(a.year)));
  if (!list.length) return null;
  return (
    <section aria-labelledby="timeline-title" data-testid="boat-timeline">
      <h2 id="timeline-title" className="text-xl md:text-2xl font-black text-[#dfddd6] uppercase mb-6">Chronologie d'entretien</h2>
      <ol className="relative border-l-2 border-[#3f4816] ml-3 space-y-6">
        {list.map((m, i) => (
          <li key={i} className="pl-6 relative">
            <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#d9fb06] border-4 border-[#1a1c1b]" aria-hidden="true" />
            <p className="text-[#d9fb06] font-black">{m.year}</p>
            <p className="text-[#dfddd6] font-semibold">{m.label}</p>
            <p className="text-xs text-[#888680] mt-1">{[m.provider, m.amount != null && fmtPrice(m.amount), m.invoice ? 'Facture disponible' : null].filter(Boolean).join(' · ')}</p>
            {m.document_url && <a href={imgUrl(m.document_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-[#d9fb06] underline">Voir le document</a>}
          </li>
        ))}
      </ol>
    </section>
  );
};

/* ---------------- Budget block ---------------- */
export const BoatBudget = ({ boat }) => {
  const bd = boat.budget || {};
  const rows = [['Prix demandé', boat.price], ['Frais de port estimatifs (par an)', bd.port_annual], ['Assurance annuelle indicative', bd.insurance_annual], ['Entretien annuel indicatif', bd.maintenance_annual], ['Consommation estimative (par an)', bd.fuel_annual], ['Travaux identifiés', bd.works]].filter(([, v]) => v != null && v !== '');
  if (rows.length <= 1) return null;
  const annual = (Number(bd.port_annual) || 0) + (Number(bd.insurance_annual) || 0) + (Number(bd.maintenance_annual) || 0) + (Number(bd.fuel_annual) || 0);
  const total3 = (Number(boat.price) || 0) + annual * 3 + (Number(bd.works) || 0);
  return (
    <section className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-6 md:p-8" aria-labelledby="budget-title" data-testid="boat-budget">
      <h2 id="budget-title" className="text-xl md:text-2xl font-black text-[#d9fb06] uppercase mb-4">Anticipez le budget réel de votre bateau</h2>
      <dl className="divide-y divide-[#3f4816]">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4 py-2 text-sm"><dt className="text-[#888680]">{k}</dt><dd className="text-[#dfddd6] font-semibold">{fmtPrice(Number(v))}</dd></div>)}
        {annual > 0 && <div className="flex justify-between gap-4 py-3 text-base"><dt className="text-[#dfddd6] font-bold">Budget total indicatif sur 3 ans</dt><dd className="text-[#d9fb06] font-black">{fmtPrice(total3)}</dd></div>}
      </dl>
      <p className="text-xs text-[#888680] mt-3">Montants indicatifs fournis à titre d'information : ils ne constituent ni un devis ni un engagement. Le simulateur interactif « Estimer mon budget sur 3 ans » arrive prochainement.</p>
    </section>
  );
};

/* ---------------- Transparency ---------------- */
export const BoatTransparency = ({ variant }) => (
  <aside className="border-l-4 border-[#d9fb06] bg-[#302f2c] rounded-r-2xl p-5" data-testid="boat-transparency">
    <h2 className="text-base font-black text-[#d9fb06] uppercase mb-2">Transparence ValidatedBoat</h2>
    <p className="text-sm text-[#dfddd6]/90 leading-relaxed">{TRANSPARENCY_TEXTS[variant] || TRANSPARENCY_TEXTS.accompagnement_vendeur}</p>
  </aside>
);
